import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { Tier } from '@traaker/shared';

const TIERS: { value: Tier; label: string; price: string }[] = [
  { value: 'lite', label: 'Lite', price: '29\u20AC/mois' },
  { value: 'pro', label: 'Pro', price: '69\u20AC/mois' },
  { value: 'enterprise', label: 'Enterprise', price: 'Sur mesure' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [tier, setTier] = useState<Tier>('lite');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/tenants/onboard', { name, domain, tier, ownerName });
      await refreshProfile();
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || 'Onboarding failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Bienvenue sur Traaker
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configurez votre espace de travail pour commencer.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Votre nom"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Nom de l'entreprise"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Domaine du site web"
              placeholder="example.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              required
              sx={{ mb: 3 }}
            />

            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Offre
            </Typography>
            <ToggleButtonGroup
              value={tier}
              exclusive
              onChange={(_, v) => v && setTier(v)}
              fullWidth
              sx={{ mb: 3 }}
            >
              {TIERS.map((t) => (
                <ToggleButton key={t.value} value={t.value} sx={{ flexDirection: 'column', py: 1.5 }}>
                  <Typography variant="body2" fontWeight={600}>{t.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.price}</Typography>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting || !name || !domain || !ownerName}
            >
              {submitting ? 'Creation...' : 'Creer mon espace'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
